import { Dispatch, useState } from 'react'
import { useQuery } from 'react-query'
import { ErrorResponse, ProductList, Product, ProductsApi } from '@citruscamps/citrus-client'
import { DefaultSearchPagination } from '../../../../../constants/pagination'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import { useRouter } from '../../../../../hooks/useRouter'
import { useToast } from '../../../../../hooks/useToast'
import { Pagination } from '../../../../../interfaces/pagination'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateListKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

const DefaultFetchProps: ProductsFetchProps = {
  sort: 'created',
  order: 'DESC',
  filter: {},
}

export interface ProductsFilterProps {
  product_id?: string[]
  on_sale?: boolean
}

export interface ProductsFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: ProductsFilterProps
}

interface IProps {
  programId?: string
  enabled?: boolean
  pagination?: Pagination
  fetchProps?: ProductsFetchProps
  overwriteFilters?: boolean
}

interface FetchProducts {
  data: Product[]
  error: ErrorResponse | null
  fetchProps: ProductsFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  setFetchProps: (value?: ProductsFetchProps) => void
  setPagination: Dispatch<Pagination>
  handleSearchProducts: (search?: string, product_type?: string[]) => Promise<Product[]>
}

/**
 * @deprecated TODO: Product financial foundation is not implemented yet
 * ⚠️ This will be removed in the future when we migrate to products
 **/
export const useFetchProducts = ({
  programId,
  enabled = true,
  pagination: initialPagination,
  overwriteFilters,
  ...props
}: IProps): FetchProducts => {
  const { requestHandler } = useRequestHandler()
  const { setToast } = useToast()
  const { pathname } = useRouter()
  const [pagination, setPagination] = useState<Pagination>(
    initialPagination || DefaultSearchPagination,
  )
  const initialFetchProps: ProductsFetchProps = props.fetchProps || DefaultFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<ProductsFetchProps>(
    `products-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const queryKeys = generateListKey({
    type: 'product',
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
  })

  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<ProductList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new ProductsApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      return await requestHandler<ProductList>(() =>
        client.findProducts(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order,
            sort: fetchProps.sort,
            product_id:
              !!fetchProps.filter?.product_id && fetchProps.filter.product_id?.length > 0
                ? fetchProps.filter.product_id
                : undefined,
            on_sale: fetchProps.filter?.on_sale || undefined,
          },
          { signal },
        ),
      )
    },
    { enabled: !!programId && enabled },
  )

  const handleSearchProducts = async (search?: string): Promise<Product[]> => {
    if (programId) {
      const client = new ProductsApi(generateApiConfig())
      try {
        const { data } = await requestHandler<ProductList>(() =>
          client.findProducts({
            program_id: programId,
            limit: search ? undefined : 3,
            skip: 0,
            search: search || undefined,
            on_sale: fetchProps.filter?.on_sale || undefined,
          }),
        )
        return data
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
        throw e
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
    return []
  }

  const _setFetchProps = (val?: ProductsFetchProps) => {
    setPagination(initialPagination || DefaultSearchPagination)
    return setFetchProps(val || DefaultFetchProps)
  }
  return {
    data: resp?.data || [],
    error,
    fetchProps,
    isError,
    isLoading,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    handleSearchProducts,
    setFetchProps: _setFetchProps,
    setPagination,
  }
}
